import useGenreOptions from "../hooks/useGenreOptions";
// src/pages/GigBoard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Stack,
  Button,
  Chip,
  Card,
  CardContent,
  CardActionArea,
  CardActions,
  Grid,
  Container,
  Skeleton,
  Paper,
  TextField,
  InputAdornment,
  Avatar,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  Snackbar,
  Alert,
  useMediaQuery,
  Drawer,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useLocation, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import StadiumIcon from "@mui/icons-material/Stadium";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PaidIcon from "@mui/icons-material/Paid";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { gigService } from "../services/resources.service";
import { hasPermission, PERMISSIONS } from "../services/authRoles";
import { canApplyToGig } from "../services/proFeatures";
import { formatCalendarDate, formatMoneyRange, getMessage, useLocale } from "../i18n/locale";
import { FilterButton, ViewModeToggle } from "../components/DiscoveryActions";
import DiscoveryMap from "../components/DiscoveryMap";
import { fetchAllMapEntities } from "../services/mapEntities";
import ProFeatureControl from "../components/ProFeatureControl";
import CommittedRadiusSlider from "../components/CommittedRadiusSlider";
import { INFINITE_RADIUS, NEAR_ME_RADIUS_KM, radiusLabel } from "../services/radius";
import { getEventProximityQuery } from "../services/eventDiscovery";
import useDebouncedSearch from "../hooks/useDebouncedSearch";

async function fetchGigs({
  query,
  genres,
  dateRange,
  radiusKm,
  budget,
  status,
  page,
  pageSize,
  latitude,
  longitude,
  accessibility,
}) {
  return gigService.list({ query, genres, dateRange, radiusKm, budget, status, page, pageSize, latitude, longitude, accessibility });
}

async function submitApplication(gigId, payload) {
  return gigService.apply(gigId, payload);
}

// ---------------- UI bits ----------------
function BudgetBadge({ min, max, currency }) {
  const localeContext = useLocale();
  return (
    <Chip
      size="small"
      icon={<PaidIcon />}
      label={formatMoneyRange(min, max, {
        locale: localeContext.locale,
        currency: currency || localeContext.currency,
      })}
      variant="outlined"
    />
  );
}

function GigCard({ gig, onOpen, onApply, applyLocked = false, applyLabel }) {
  const localeContext = useLocale();
  const { locale, currency } = localeContext;
  const eventDate = formatCalendarDate(gig.date, localeContext);
  const deadline = formatCalendarDate(gig.appDeadline, localeContext);
  const budget = formatMoneyRange(gig.budgetMin, gig.budgetMax, { locale, currency: gig.currency || currency });

  return (
    <Card variant="outlined" sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <CardActionArea onClick={() => onOpen?.(gig)}>
        <CardContent sx={{ p: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              badgeContent={
                gig.venue.hasPA ? (
                  <Avatar sx={{ width: 18, height: 18, bgcolor: "success.main" }}>
                    <CheckCircleIcon sx={{ fontSize: 14, color: "white" }} />
                  </Avatar>
                ) : null
              }
            >
              <Avatar src={gig.venue.avatar} alt={gig.venue.name}>
                <StadiumIcon />
              </Avatar>
            </Badge>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle1" fontWeight={800} noWrap>
                {gig.title}
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.25 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <CalendarMonthIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                  <Typography variant="body2">{eventDate}</Typography>
                  <Typography variant="body2" color="text.disabled">·</Typography>
                  <Typography variant="body2" fontWeight={700}>{budget}</Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <LocationOnIcon sx={{ fontSize: 17, color: "text.secondary" }} />
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {gig.venue.name} · {gig.venue.city}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={0.75} sx={{ mt: 1.25, flexWrap: "wrap", rowGap: 0.75 }}>
                {gig.genres.map((g) => (
                  <Chip key={g} label={g} size="small" sx={{ height: 24 }} />
                ))}
                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: "center" }}>
                  {gig.applicants} applicants
                </Typography>
              </Stack>
            </Box>
          </Stack>

          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: 1, borderColor: "divider" }}>
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>Backline</Box>
              {" · "}{gig.backline}
            </Typography>
            <Stack direction="row" justifyContent="space-between" gap={1} sx={{ mt: 0.5 }}>
              <Typography variant="body2" color="text.secondary">{gig.paymentTerms}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                Apply by {deadline}
              </Typography>
            </Stack>
          </Box>
        </CardContent>
      </CardActionArea>
      {onApply && <CardActions sx={{ px: 2, pb: 2, pt: 0, mt: "auto", justifyContent: "flex-end" }}>
        <ProFeatureControl locked={applyLocked} label={applyLabel}>
          <span>
            <Button size="small" variant="contained" endIcon={<SendIcon />} disabled={applyLocked} onClick={() => onApply(gig)}>
              {applyLabel}
            </Button>
          </span>
        </ProFeatureControl>
      </CardActions>}
    </Card>
  );
}

function ApplyDialog({ open, gig, onClose, onSubmit, artistProfile }) {
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const localeContext = useLocale();

  useEffect(() => {
    if (open) {
      setMessage("");
      setFiles([]);
      setSubmitting(false);
    }
  }, [open]);

  const handleFiles = (e) => {
    setFiles(Array.from(e.target.files || []));
  };

  const submit = async () => {
    if (!gig) return;
    setSubmitting(true);
    try {
      await onSubmit({
        message,
        files,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <DialogTitle>Apply to “{gig?.title}”</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            <Chip icon={<StadiumIcon />} label={gig?.venue?.name} />
            <Chip icon={<CalendarMonthIcon />} label={gig ? formatCalendarDate(gig.date, localeContext) : ""} />
            <BudgetBadge min={gig?.budgetMin} max={gig?.budgetMax} currency={gig?.currency} />
          </Stack>

          <TextField
            label="Message"
            placeholder="Introduce your act, fee expectations, and any notes."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            multiline
            minRows={6}
            fullWidth
          />

          <Stack direction="row" spacing={1}>
            <label htmlFor="gig-attachments">
              <input
                id="gig-attachments"
                type="file"
                multiple
                hidden
                onChange={handleFiles}
                accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
              />
              <Button variant="outlined" startIcon={<AttachFileIcon />} component="span">
                Attach (EPK/Rider)
              </Button>
            </label>
          </Stack>

          {!!files.length && (
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Attachments
              </Typography>
              <Stack spacing={0.5}>
                {files.map((f, i) => (
                  <Typography key={i} variant="body2" color="text.secondary">
                    {f.name} — {(f.size / 1024).toFixed(0)} KB
                  </Typography>
                ))}
              </Stack>
            </Paper>
          )}

          {submitting && <LinearProgress />}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button variant="contained" startIcon={<SendIcon />} onClick={submit} disabled={submitting}>
          Send application
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ---------------- Main Page ----------------
export default function GigBoard({
  onNavigate,
  onChangeRegion,
  region = "Near me",
  discoveryLocation,
  discoveryRadiusKm,
  onChangeDiscoveryRadius,
  artistProfile,
  user,
}) {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const { locale } = useLocale();
  const applyLabel = getMessage(locale, "gigApply");
  const mayApplyByRole = hasPermission(user, PERMISSIONS.APPLY_TO_GIG);
  const applyLocked = mayApplyByRole && !canApplyToGig(user);

  // search & filters
  const { inputValue: searchInput, setInputValue: setSearchInput, searchValue: query, submitSearch } = useDebouncedSearch();
  const [genres, setGenres] = useState(["All"]);
  const [dateRange, setDateRange] = useState("All"); // Today | Weekend | All
  const radiusKm = discoveryRadiusKm;
  const setRadiusKm = onChangeDiscoveryRadius; // 2 | 10 | 25 | 100
  const [budget, setBudget] = useState("any"); // lt1500 | 1500-3000 | gt3000 | any
  const [status, setStatus] = useState("OPEN"); // OPEN | CLOSED | ANY
  const [accessibleOnly, setAccessibleOnly] = useState(false);
  const [mapMode, setMapMode] = useState(false);

  // data & pagination
  const [gigs, setGigs] = useState([]);
  const [mapGigs, setMapGigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const pageSize = 6;

  // UI state
  const [applyGig, setApplyGig] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });

  useEffect(() => {
    if (!location.state?.publishedGigId || !location.state?.publishedEventId) return;
    setSnack({
      open: true,
      msg: getMessage(locale, "gigPublishSuccess"),
      severity: "success",
    });
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, locale, navigate]);

  // NEW: filters drawer
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    let active = true;
    fetchAllMapEntities(gigService.list, {
      query,
      genres,
      dateRange,
      budget,
      status,
      accessibility: accessibleOnly ? ["Wheelchair"] : [],
      ...getEventProximityQuery(radiusKm, discoveryLocation),
    }).then((items) => {
      if (active) setMapGigs(items);
    });
    return () => { active = false; };
  }, [query, genres, dateRange, radiusKm, budget, status, accessibleOnly, discoveryLocation]);

  const genreOptions = useGenreOptions();

  const runFetch = async ({ reset = false } = {}) => {
    setLoading(true);
    const nextPage = reset ? 1 : page;
    const res = await fetchGigs({
      query,
      genres,
      dateRange,
      radiusKm,
      budget,
      status,
      accessibility: accessibleOnly ? ["Wheelchair"] : [],
      page: nextPage,
      pageSize,
      ...getEventProximityQuery(radiusKm, discoveryLocation),
    });
    setLoading(false);
    setHasMore(res.hasMore);
    if (reset) {
      setPage(1);
      setGigs(res.data);
    } else {
      setGigs((prev) => [...prev, ...res.data]);
    }
  };

  useEffect(() => {
    runFetch({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, JSON.stringify(genres), dateRange, radiusKm, budget, status, accessibleOnly, discoveryLocation]);

  // genre helpers for Autocomplete multiple
  const handleGenresChange = (_, values) => {
    if (!values?.length) return setGenres(["All"]);
    if (values.includes("All")) return setGenres(["All"]);
    setGenres(values);
  };

  // active filters count (for button badge)
  const activeCount = useMemo(() => {
    let n = 0;
    if (dateRange !== "All") n++;
    if (!(genres.length === 1 && genres[0] === "All")) n++;
    if (radiusKm !== INFINITE_RADIUS) n++;
    if (budget !== "any") n++;
    if (status !== "OPEN") n++; // OPEN is the default here
    if (accessibleOnly) n++;
    return n;
  }, [dateRange, genres, radiusKm, budget, status, accessibleOnly]);

  const subtitle = useMemo(() => {
    const parts = [region];
    if (dateRange !== "All") parts.push(dateRange);
    parts.push(radiusLabel(radiusKm));
    if (genres.length && !genres.includes("All")) parts.push(genres.join(", "));
    if (budget !== "any")
      parts.push(
        budget === "lt1500" ? "< R1,500" : budget === "gt3000" ? "> R3,000" : "R1,500–R3,000"
      );
    if (status !== "ANY") parts.push(status);
    if (accessibleOnly) parts.push("Accessible");
    return parts.join(" • ");
  }, [region, dateRange, radiusKm, genres, budget, status, accessibleOnly]);

  const resetFilters = () => {
    setDateRange("All");
    setGenres(["All"]);
    setRadiusKm(INFINITE_RADIUS);
    setBudget("any");
    setStatus("OPEN");
    setAccessibleOnly(false);
  };

  const handleApply = async ({ message, files }) => {
    if (!applyGig || !canApplyToGig(user)) return;
    const payload = {
      artistId: artistProfile?.id,
      message,
      attachments: files.map((f) => ({ name: f.name, size: f.size })),
    };
    const res = await submitApplication(applyGig.id, payload);
    if (res.ok) {
      setSnack({ open: true, msg: "Application sent!", severity: "success" });
      setApplyGig(null);
    } else {
      setSnack({ open: true, msg: "Application failed. Try again.", severity: "error" });
    }
  };

  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <AppBar position="sticky" elevation={0} color="inherit">
        <Toolbar sx={{ gap: 1 }}>
          <IconButton edge="start" aria-label="back" onClick={() => onNavigate?.("/events")}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ fontWeight: 900, letterSpacing: 0.2 }}>
            Gig Board
          </Typography>

          <Box sx={{ flex: 1, mx: 1 }} component="form" onSubmit={submitSearch}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search gigs (e.g. indie, brunch, Cape Town)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <ViewModeToggle
            mapMode={mapMode}
            onChange={setMapMode}
            sx={{ display: { xs: "none", sm: "inline-flex" } }}
          />
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 2 }}>
        {/* Ultra-compact bar: location + quick date */}
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: 1 }}>
            <Chip
              icon={<LocationOnIcon />}
              label={region}
              variant={radiusKm === INFINITE_RADIUS ? "outlined" : "filled"}
              clickable
              onClick={async () => {
                if (radiusKm !== INFINITE_RADIUS) {
                  setRadiusKm(INFINITE_RADIUS);
                  return;
                }
                if (discoveryLocation) {
                  setRadiusKm(NEAR_ME_RADIUS_KM);
                  return;
                }
                const location = await onChangeRegion?.();
                if (location) setRadiusKm(NEAR_ME_RADIUS_KM);
              }}
            />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={dateRange}
              onChange={(_, v) => v && setDateRange(v)}
              sx={{
                ml: 0.5,
                borderRadius: 999,
                "& .MuiToggleButton-root": { textTransform: "none", px: 1.25 },
              }}
            >
              <ToggleButton value="Today">Today</ToggleButton>
              <ToggleButton value="Weekend">Weekend</ToggleButton>
              <ToggleButton value="All">All</ToggleButton>
            </ToggleButtonGroup>
            <FilterButton
              count={activeCount}
              compactOnMobile
              onClick={() => setFiltersOpen(true)}
              sx={{ ml: "auto" }}
            />
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {subtitle ? `Showing: ${subtitle}` : "Showing all gigs"}
          </Typography>
        </Stack>

        {mapMode && (
          <Box sx={{ mb: 2 }}>
            <DiscoveryMap
              items={mapGigs}
              getCoordinates={(gig) => gig.venue?.geo}
              getLabel={(gig) => gig.title}
              onSelect={(gig) => onNavigate?.(`/events/${gig.eventId ?? gig.id}`)}
              labelMessageKey="gigMapLabel"
              height={320}
            />
          </Box>
        )}

        {/* List */}
        <Grid container spacing={2}>
          {(loading && gigs.length === 0 ? Array.from({ length: 6 }) : gigs).map((g, idx) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={g?.id ?? idx}>
              {g ? (
                <GigCard
                  gig={g}
                  onOpen={(gig) => onNavigate?.(`/events/${gig.eventId ?? gig.id}`)}
                  onApply={mayApplyByRole ? (gig) => setApplyGig(gig) : undefined}
                  applyLocked={applyLocked}
                  applyLabel={applyLabel}
                />
              ) : (
                <Skeleton variant="rounded" height={220} sx={{ borderRadius: 3 }} />
              )}
            </Grid>
          ))}
        </Grid>

        {/* Pagination */}
        <Stack alignItems="center" sx={{ py: 3 }}>
          {hasMore ? (
            <Button
              size="large"
              disabled={loading}
              onClick={async () => {
                setPage((p) => p + 1);
                setLoading(true);
                const res = await fetchGigs({
                  query,
                  genres,
                  dateRange,
                  radiusKm,
                  budget,
                  status,
                  accessibility: accessibleOnly ? ["Wheelchair"] : [],
                  page: page + 1,
                  pageSize,
                  ...getEventProximityQuery(radiusKm, discoveryLocation),
                });
                setLoading(false);
                setHasMore(res.hasMore);
                setGigs((prev) => [...prev, ...res.data]);
              }}
            >
              {loading ? "Loading…" : "Load more"}
            </Button>
          ) : (
            <Typography variant="body2" color="text.secondary">
              You’ve reached the end.
            </Typography>
          )}
        </Stack>
      </Container>

      {/* Apply Dialog */}
      <ApplyDialog
        open={Boolean(applyGig)}
        gig={applyGig}
        onClose={() => setApplyGig(null)}
        onSubmit={handleApply}
        artistProfile={artistProfile}
      />

      {/* Snackbars */}
      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>

      {/* Filters Drawer (bottom on mobile, right on desktop) */}
      <Drawer
        anchor={isMdUp ? "right" : "bottom"}
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        PaperProps={{
          sx: {
            width: { md: 360 },
            borderTopLeftRadius: { xs: 16, md: 0 },
            borderTopRightRadius: { xs: 16, md: 0 },
            p: 2,
            gap: 2,
          },
        }}
      >
        <Stack spacing={2}>
          <Typography variant="h6" fontWeight={800}>
            Filters
          </Typography>

          {/* Date */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Date
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={dateRange}
              onChange={(_, v) => v && setDateRange(v)}
            >
              <ToggleButton value="Today">Today</ToggleButton>
              <ToggleButton value="Weekend">Weekend</ToggleButton>
              <ToggleButton value="All">All</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Genres (multi-select, compact) */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Genres
            </Typography>
            <Autocomplete
              multiple
              size="small"
              options={genreOptions}
              value={genres.includes("All") ? [] : genres}
              onChange={handleGenresChange}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select genres" />
              )}
              renderTags={(value, getTagProps) =>
                value.slice(0, 3).map((option, index) => (
                  <Chip key={option} label={option} size="small" {...getTagProps({ index })} />
                ))
              }
            />
            <Button
              size="small"
              sx={{ mt: 0.5 }}
              onClick={() => setGenres(["All"])}
            >
              Clear genres
            </Button>
          </Box>

          {/* Radius */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Radius: {radiusLabel(radiusKm)}
            </Typography>
            <CommittedRadiusSlider value={radiusKm} onChange={setRadiusKm} />
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={accessibleOnly}
                onChange={(event) => setAccessibleOnly(event.target.checked)}
              />
            }
            label="Accessible venues only"
          />

          {/* Budget */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Budget
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={budget}
              onChange={(_, v) => v && setBudget(v)}
            >
              <ToggleButton value="any">Any</ToggleButton>
              <ToggleButton value="lt1500">&lt; R1,500</ToggleButton>
              <ToggleButton value="1500-3000">R1,500–R3,000</ToggleButton>
              <ToggleButton value="gt3000">&gt; R3,000</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Status */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Status
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={status}
              onChange={(_, v) => v && setStatus(v)}
            >
              <ToggleButton value="OPEN">Open</ToggleButton>
              <ToggleButton value="CLOSED">Closed</ToggleButton>
              <ToggleButton value="ANY">Any</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Stack direction="row" justifyContent="space-between" gap={1} sx={{ pt: 1 }}>
            <Button onClick={resetFilters}>Reset</Button>
            <Button variant="contained" onClick={() => setFiltersOpen(false)}>
              Apply
            </Button>
          </Stack>
        </Stack>
      </Drawer>

      <Box sx={{ height: 16 }} />
    </Box>
  );
}
